import React, { useRef, useLayoutEffect, useEffect } from 'react';
import { Box, Typography, Container, Button } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import gsap from 'gsap';
import SeoTagsManager from '../../utils/SeoTagsManager';
import { headingLetterSpacing } from '../../utils/headingTypography';

import ProviderTileComponentSummary from '../../components/ProviderTile/ProviderTileComponentSummary';
import { toSentenceCase } from '../../utils/casing';
import { useMobileContext } from '../../contexts/MobileContext';
import { pushThankYouView } from '../../utils/dataLayer';

const FLEXICARE_LOGO =
  'https://assets.askhippo.co.za/Brands/partnerbrands/flexicare.png';

function FlexicareThankYou() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isMobile } = useMobileContext();

  // Data for the provider tile — no premium/amount so the logo renders centred.
  const tileData = { FriendlyName: 'Flexicare', LogoURL: FLEXICARE_LOGO };

  const staggerRef = useRef(null);

  useLayoutEffect(() => {
    const ctx = gsap.context(() => {
      const root = staggerRef.current;
      if (!root) return;

      // Immediately hide to prevent flash
      const all = root.querySelectorAll('.stagger-in');
      if (all.length) gsap.set(all, { opacity: 0, y: 30 });

      // Run slightly after DOM settles
      setTimeout(() => {
        const tl = gsap.timeline({
          defaults: { duration: 0.6, ease: 'power3.out' },
        });

        tl.to('.stagger-title', { opacity: 1, y: 0 })
          .to('.stagger-provider', { opacity: 1, y: 0 }, '+=0.1')
          .to('.stagger-desc', { opacity: 1, y: 0 }, '+=1.3')
          .to('.stagger-btn-secondary', { opacity: 1, y: 0 }, '+=0.08');
      }, 30);
    }, staggerRef);

    return () => ctx.revert();
  }, []);

  useEffect(() => {
    pushThankYouView({
      product: 'Flexicare',
      partner_name: 'Flexicare',
    });
  }, []);

  const HippoAvatar =
    'https://assets.askhippo.co.za/avatars/v2/Thumbs-up-v2.gif';

  return (
    <>
      {SeoTagsManager.render(pathname)}
      <Container>
        <Box
          ref={staggerRef}
          sx={{
            pt: isMobile ? '37px' : '32px',
            width: isMobile ? '343px' : '448px',
            mx: 'auto',
            textAlign: 'center',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}
        >
          {/* Avatar is NOT part of the stagger sequence */}
          <img
            src={HippoAvatar}
            alt="Hippo Hand"
            style={{ width: '124px', height: '124px', objectFit: 'contain' }}
          />

          {/* Title */}
          <Typography
            variant="h5"
            className="stagger-in stagger-title"
            sx={{
              fontFamily: 'Inter',
              fontWeight: 700,
              width: '100%',
              fontSize: isMobile ? '2rem' : '3rem',
              lineHeight: isMobile ? '34px' : '52px',
              letterSpacing: headingLetterSpacing({
                xs: '2rem',
                md: '3rem',
              }),
              color: '#162F3B',
            }}
          >
            Great Choice!
          </Typography>

          {/* ProviderTileComponentSummary — logo centred, no price */}
          <Box
            className="stagger-in stagger-provider"
            sx={{
              display: 'flex',
              justifyContent: 'center',
              mt: '32px',
              mb: '32px',
            }}
          >
            <ProviderTileComponentSummary data={tileData} />
          </Box>

          {/* Description */}
          <Box
            className="stagger-in stagger-desc"
            sx={{ mt: '16px', width: '100%' }}
          >
            <Typography
              sx={{
                fontFamily: 'Inter',
                fontWeight: 400,
                fontSize: isMobile ? '1.25rem' : '1.5rem',
                lineHeight: isMobile ? '24px' : '28px',
                letterSpacing: '-0.03em',
                color: '#162F3B',
                maxWidth: '100%',
              }}
            >
              We’ve sent your details to Flexicare. They’ll call to confirm your
              details and cover.
            </Typography>
          </Box>

          {/* Secondary Button */}
          <Box
            className="stagger-in stagger-btn-secondary"
            sx={{ mt: isMobile ? '32px' : '58px' }}
          >
            <Button
              onClick={() => navigate('/')}
              sx={{
                fontFamily: 'Inter',
                fontWeight: 600,
                fontSize: '0.875rem',
                lineHeight: '1.25rem',
                letterSpacing: '-0.002em',
                verticalAlign: 'middle',
                color: '#A2ACB1',
                cursor: 'pointer',
                px: '8px',
                py: '4px',
                borderRadius: '8px',
                textDecoration: 'none',
                textTransform: 'none',
              }}
            >
              {toSentenceCase('Take Me Home')}
            </Button>
          </Box>
        </Box>
      </Container>
    </>
  );
}

export default FlexicareThankYou;
